import { produce } from 'immer'
import { isNil } from 'lodash'

/*
 * ⚠ ONE ENTRY PER CRITERION. `OptimizePromptButton` renders inside every criterion's form and a scorecard can have
 * a finished run on any number of them at once, so runs are keyed by criterion: each reads and writes only its own
 * entry. ⚠ Do NOT collapse this to one run plus an owner field — that shape can only ever show one result.
 *
 * Keyed by `scid` rather than `criterionId` because `scorecard_criteria` is insert-only: `id` is the version and is
 * re-minted on every save, while `scid` is stable and exists even on a criterion that has never been saved.
 */

// The lifecycle of an optimization run, mirroring `optimizations.status` in summary-service. Constants rather than
// literals because the panel branches on all of them and a typo would silently render the wrong step.
export const RUN_STATUS = {
  // CLIENT-ONLY; the server never returns it. Starting a run is two sequential POSTs, so this is what the panel
  // renders against in between — the click has an effect immediately rather than when the server answers.
  STARTING: 'starting',
  QUEUED: 'queued',
  RUNNING: 'running',
  // The run posed questions and is waiting for a person. Unreachable in this release — the shipping ground-truth
  // method (`task_pass`) never asks anything — but the server can still return it if the method is flipped, so the
  // panel recognises it rather than rendering a finished run that has no candidates.
  AWAITING_ANSWERS: 'awaiting_answers',
  DONE: 'done',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
}

// While the panel should be on screen: STARTING has no id yet and nothing to poll, so the two lists differ.
export const ACTIVE_STATUSES = [
  RUN_STATUS.STARTING,
  RUN_STATUS.QUEUED,
  RUN_STATUS.RUNNING,
  RUN_STATUS.AWAITING_ANSWERS,
]
export const POLLABLE_STATUSES = [RUN_STATUS.QUEUED, RUN_STATUS.RUNNING]
export const REGRESSION_SCOPE = 'regression'

// Why a run finished without writing candidates, matching `live.STOPPED_*`. Needed because an empty candidate list
// also means "every version failed to produce a usable answer" — a fault, and the opposite of this.
export const STOPPED_EARLY = {
  // The prompt already matched the reference on every sampled call, so no rewrite could beat it — only tie.
  AT_CEILING: 'incumbent_at_ceiling',
}

// The sampling stages the server reports, matching `sampler.SAMPLING_*`. Sampling is the one stage with no rows for
// the API to derive progress from, so it reports its own counts and these say which one they describe.
export const SAMPLING_STAGE = {
  FILTERING: 'filtering',
  GATING: 'gating',
  ANSWERING: 'answering',
}

/** One criterion's run. The shape a criterion with no run reads as, so consumers never branch on absence. */
export const EMPTY_RUN = {
  optimizationId: null,
  criterionId: null,
  status: null,
  // The prompt as it was when Optimize was pressed. Snapshotted because applying a candidate overwrites
  // `user_prompt`, so a diff read live off the form would compare the new prompt to itself. Also what "put my
  // prompt back" restores.
  originalPrompt: null,
  // Milestones the run passes in order, read off rows the pipeline has already written. Coarse on purpose: the
  // server reports what it can honestly count, and only the labelling stage has a denominator.
  progress: {
    nSampled: 0,
    groundTruthStatus: null,
    nLabeled: 0,
    nCandidates: 0,
    // Sampling is the exception, and reports its own counts — nothing it produces exists as a row until it is over.
    sampling: {
      stage: null,
      nFilterMatched: null,
      nChecked: null,
      nEligible: null,
      nTarget: null,
    },
  },
  rankBy: null,
  // How the prompt being optimized scored on the same reference — without it a winner is unreadable, because the
  // question is never "is this good" but "is this better than what we run today".
  incumbentMetrics: null,
  candidates: [],
  winner: null,
  costUsd: null,
  // The model the run measured on — both columns are this model. Whatever the editor's dropdown held at click
  // time, which need not be the model the criterion runs today. Null until the first poll.
  targetModel: null,
  targetEffort: null,
  targetModelId: null,
  // Why a failed run failed, as the server recorded it. Null on every other status.
  failureReason: null,
  // Why a finished run wrote no candidates, when that was the point. Null on every ordinary run — see STOPPED_EARLY.
  stoppedEarly: null,
  // Whether the scorecard's eligibility settings have changed since this ran. The result stays usable — it just
  // describes the calls the criterion covered at the time, which nothing else on screen would reveal.
  eligibilityChanged: null,
  // Set when a candidate's text is applied, cleared once the scorecard save that follows is recorded. Per criterion
  // because two criteria can each have a suggestion sitting in their form waiting for one save.
  pendingAdoption: null,
  loading: {
    start: false,
    poll: false,
    cancel: false,
  },
  error: null,
}

export const initialState = {
  // criterion `scid` -> EMPTY_RUN-shaped entry.
  byCriterion: {},
  // The per-call drill-in and its spinner, keyed by candidate id — those are unique across criteria, so these stay
  // flat rather than nesting under a criterion that would only ever hold one candidate's worth.
  changedCalls: {},
  callsLoading: {},
  callsError: {},
  // Optimization ids dismissed in this session. Dismissal is recorded server-side and `/pending` reports it, so this
  // covers only the gap before that write lands, when recovery would still see the run as live.
  // ⚠ Ids, not a per-criterion flag: a flag would also suppress the next run on that criterion.
  dismissedIds: [],
}

/**
 * One criterion's run, or the empty shape when it has none.
 *
 * ⚠ Tolerates a missing slice on purpose. This lives inside the criterion form, which many existing tests render
 * through `redux-mock-store` — that store is exactly the state a test declares, with no reducer defaults, so tests
 * written before this feature have no `promptimizer` key at all and reading it raw takes the whole form down.
 */
export const selectRun = (state, criteriaScid) => {
  if (isNil(criteriaScid)) {
    return EMPTY_RUN
  }

  return state?.promptimizer?.byCriterion?.[criteriaScid] ?? EMPTY_RUN
}

export const SET_LOADING = 'promptimizer/setLoading'
export const SET_ERROR = 'promptimizer/setError'
export const SET_RUN = 'promptimizer/setRun'
export const SET_STATUS = 'promptimizer/setStatus'
export const SET_CHANGED_CALLS = 'promptimizer/setChangedCalls'
export const SET_CALLS_LOADING = 'promptimizer/setCallsLoading'
export const SET_CALLS_ERROR = 'promptimizer/setCallsError'
export const RESET_RUN = 'promptimizer/resetRun'
export const SET_PENDING_ADOPTION = 'promptimizer/setPendingAdoption'
export const RECORD_DISMISSED = 'promptimizer/recordDismissed'

// Every run-scoped action carries the criterion it is about, so a write can never land on another criterion's entry.
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const setRun = (payload) => ({ type: SET_RUN, payload })
export const setStatus = (payload) => ({ type: SET_STATUS, payload })
export const setChangedCalls = (payload) => ({ type: SET_CHANGED_CALLS, payload })
export const setCallsLoading = (payload) => ({ type: SET_CALLS_LOADING, payload })
export const setCallsError = (payload) => ({ type: SET_CALLS_ERROR, payload })
export const resetRun = (payload) => ({ type: RESET_RUN, payload })
export const setPendingAdoption = (payload) => ({ type: SET_PENDING_ADOPTION, payload })
export const recordDismissed = (payload) => ({ type: RECORD_DISMISSED, payload })

export default function promptimizerReducer(state = initialState, action) {
  return produce(state, (newState) => {
    const { payload } = action
    // Every run-scoped action names its criterion; anything else is a bug in the caller rather than something to
    // absorb, so the write is dropped instead of landing somewhere arbitrary.
    const scid = payload?.criteriaScid
    const entry = () => {
      if (isNil(newState.byCriterion[scid])) {
        newState.byCriterion[scid] = { ...EMPTY_RUN }
      }

      return newState.byCriterion[scid]
    }

    switch (action.type) {
      case SET_LOADING:
        if (!isNil(scid)) {
          entry().loading = { ...entry().loading, ...payload.loading }
        }
        break
      case SET_ERROR:
        if (!isNil(scid)) {
          entry().error = payload.error
        }
        break
      case SET_RUN:
        if (!isNil(scid)) {
          Object.assign(entry(), payload.run)
          // A successful read clears a previous error: the run is speaking for itself now.
          entry().error = null
        }
        break
      case SET_STATUS:
        // Status alone, leaving `error` in place — the reason this exists separately from SET_RUN, which clears it.
        // A failed start has to put the button back AND keep the reason on screen.
        if (!isNil(scid)) {
          entry().status = payload.status
        }
        break
      case SET_CHANGED_CALLS:
        newState.changedCalls[payload.candidateId] = payload.calls
        break
      case SET_CALLS_LOADING:
        newState.callsLoading[payload.candidateId] = payload.loading
        break
      case SET_CALLS_ERROR:
        newState.callsError[payload.candidateId] = payload.error
        break
      case SET_PENDING_ADOPTION:
        if (!isNil(scid)) {
          entry().pendingAdoption = payload.pendingAdoption
        }
        break
      case RECORD_DISMISSED:
        if (!newState.dismissedIds.includes(payload.optimizationId)) {
          newState.dismissedIds.push(payload.optimizationId)
        }
        break
      case RESET_RUN:
        // Only this criterion's entry. `pendingAdoption` goes with it: starting a fresh run means the earlier
        // suggestion is no longer what is being applied, so carrying it forward would attribute a later save to
        // the wrong run.
        if (!isNil(scid)) {
          delete newState.byCriterion[scid]
        }
        break
      default:
        break
    }

    return newState
  })
}
